import { Http } from '../http';
import moment from 'moment/moment';
import AxiosRetry from '../../utils/axios/axios-retry';
import { getLogDataFromUserContext } from '../clickstream-service/utils';
import {
  getOfferingApi,
  getPlayerApi
} from '../../config/kambi/api-config/kambi-config';
import {
  OfferingApiAxiosInstance,
  PlayerApiAxiosInstance,
  ExtendSessionAxiosInstance
} from './axios-instance';

const numberOfExtendSessionRetries = 10,
  delayBetweenRetries = moment.duration(5, 'seconds').asMilliseconds();
const extendSessionRetry = AxiosRetry({
  numberOfRetries: numberOfExtendSessionRetries,
  delay: delayBetweenRetries
});

/**
 * @namespace KambiApi
 */
export default {
  getEventsForHighlightsStrip: function(
    groupIds,
    locale,
    market,
    offering,
    logData,
    useProdApis = false
  ) {
    const kambiApiUrl = getOfferingApi(useProdApis);
    const url = `${kambiApiUrl}/${offering}/betoffer/group/${groupIds.join(
      ','
    )}`;
    const params = {
      market,
      lang: locale,
      onlyMain: true,
      excludeLive: true,
      excludePrematch: false,
      includeParticipants: false
    };

    return new Http(OfferingApiAxiosInstance.get())
      .get(logData, url, params)
      .then(response => response.data);
  },

  getEventDetails: function(
    eventId,
    locale,
    market,
    offering,
    logData,
    useProdApis = false
  ) {
    const kambiApiUrl = getOfferingApi(useProdApis);
    const url = `${kambiApiUrl}/${offering}/betoffer/event/${eventId}`;
    // don't include participants, and get only one event (not exactly necessary) to reduce the size of the response
    const params = {
      lang: locale,
      market,
      includeParticipants: false,
      range_size: 1
    };

    return new Http(OfferingApiAxiosInstance.get())
      .get(logData, url, params, true)
      .then(response => response.data);
  },

  getOddsLadder: function(userContext, useProdApis = false) {
    const { offering } = userContext;
    const kambiApiUrl = getOfferingApi(useProdApis);
    const url = `${kambiApiUrl}/${offering}/oddsLadder`;
    const logData = getLogDataFromUserContext(userContext);

    return new Http(OfferingApiAxiosInstance.get())
      .get(logData, url, {}, true)
      .then(response => response.data);
  },

  getBetOfferOutcome: function(selectionId, userContext, useProdApis = false) {
    const { offering } = userContext;
    const kambiApiUrl = getOfferingApi(useProdApis);
    const url = `${kambiApiUrl}/${offering}/betoffer/outcome`;
    const logData = getLogDataFromUserContext(userContext);
    const params = {
      id: selectionId
    };
    return new Http(OfferingApiAxiosInstance.get())
      .get(logData, url, params, true)
      .then(response => response.data);
  },

  getQuickLinks: function(
    locale,
    market,
    offering,
    logData,
    useProdApis = false
  ) {
    const kambiApiUrl = getOfferingApi(useProdApis);
    const url = `${kambiApiUrl}/${offering}/group/highlight`;
    const params = { lang: locale, market, depth: 1 };

    return new Http(OfferingApiAxiosInstance.get())
      .get(logData, url, params, true)
      .then(response => response.data);
  },

  getSearchResults: function(
    locale,
    market,
    offering,
    term,
    logData,
    useProdApis = false
  ) {
    const kambiApiUrl = getOfferingApi(useProdApis);
    const url = `${kambiApiUrl}/${offering}/term/search`;
    const params = { lang: locale, market, term };

    return new Http(OfferingApiAxiosInstance.get())
      .get(logData, url, params, true)
      .then(response => response.data);
  },

  getSports: function(locale, market, offering, logData, useProdApis = false) {
    const kambiApiUrl = getOfferingApi(useProdApis);
    const url = `${kambiApiUrl}/${offering}/group`;
    const params = { lang: locale, market, depth: 3 };

    return new Http(OfferingApiAxiosInstance.get())
      .get(logData, url, params, true)
      .then(response => response.data);
  },

  /**
   * Get live events.
   * @memberOf KambiApi
   * @param {string} locale - Defines what language should be used for localized texts. Example en_GB, fr_FR.
   * @param {string} market - Defines the geographical market that the player is registered in. Example GB, FR.
   * @param {string} offering - Unique identifier assigned to the API user. Example: kambi.
   * @param {*} logData - log data TODO needs an object type.
   * @param {boolean} [useProdApis=false] - Uses prod API when true.
   * @return {Promise<KLiveEventResponse>}
   */
  getLiveEvents: function(
    locale,
    market,
    offering,
    logData,
    useProdApis = false
  ) {
    const kambiApiUrl = getOfferingApi(useProdApis);
    const url = `${kambiApiUrl}/${offering}/event/live/open`;
    const params = { lang: locale, market, depth: 1 };

    return new Http(OfferingApiAxiosInstance.get())
      .get(logData, url, params, true)
      .then(response => response.data);
  },

  getEnhancedSpecials: function(
    locale,
    market,
    offering,
    logData,
    useProdApis = false
  ) {
    const kambiApiUrl = getOfferingApi(useProdApis);
    const url = `${kambiApiUrl}/${offering}/listView/888_specials/enhanced_accas`;
    const params = { lang: locale, market, includeParticipants: false };

    return new Http(OfferingApiAxiosInstance.get())
      .get(logData, url, params, true)
      .then(response => response.data);
  },

  getListViewData: function(
    groupId,
    locale,
    market,
    offering,
    logData,
    range_start = 0,
    useProdApis = false
  ) {
    const kambiApiUrl = getOfferingApi(useProdApis);
    const url = `${kambiApiUrl}/${offering}/betoffer/group/${groupId}`;
    const params = { lang: locale, market, range_start };

    return new Http(OfferingApiAxiosInstance.get())
      .get(logData, url, params, true)
      .then(response => response.data);
  },

  getBetOffersByGroupId: function(
    groupId,
    userContext,
    type,
    useProdApis = false
  ) {
    const { locale, market, offering } = userContext;
    const kambiApiUrl = getOfferingApi(useProdApis);
    const url = `${kambiApiUrl}/${offering}/betoffer/group/${groupId}`;
    const params = { lang: locale, market, type };
    const logData = getLogDataFromUserContext(userContext);

    return new Http(OfferingApiAxiosInstance.get())
      .get(logData, url, params, true)
      .then(response => response.data);
  },

  getEventsByEventGroupId: function(groupId, userContext, useProdApis = false) {
    const { locale, market, offering } = userContext;
    const kambiApiUrl = getOfferingApi(useProdApis);
    const url = `${kambiApiUrl}/${offering}/event/group/${groupId}`;
    const params = { lang: locale, market, includeParticipants: false };
    const logData = getLogDataFromUserContext(userContext);

    return new Http(OfferingApiAxiosInstance.get())
      .get(logData, url, params, true)
      .then(response => response.data);
  },

  /**
   * Get meetings for sport category.
   * @memberOf KambiApi
   * @param {string} sportCategory - Code for sports. Example: horse_racing
   * @param {string} locale - Defines what language should be used for localized texts. Example en_GB, fr_FR.
   * @param {string} market - Defines the geographical market that the player is registered in. Example GB, FR.
   * @param {string} offering - Unique identifier assigned to the API user. Example: kambi.
   * @param {*} logData - log data TODO needs an object type.
   * @param {boolean} [useProdApis=false] - Uses prod API when true.
   * @return {Promise<KMeeting[]>} - Array of meetings that contain racing events.
   */
  getMeetingsForSportCategory: function(
    sportCategory,
    locale,
    market,
    offering,
    logData,
    useProdApis = false
  ) {
    const kambiApiUrl = getOfferingApi(useProdApis);
    const url = `${kambiApiUrl}/${offering}/meeting/${sportCategory}`;
    const params = { lang: locale, market };

    return new Http(OfferingApiAxiosInstance.get())
      .get(logData, url, params, true)
      .then(response => response.data);
  },

  /**
   * Get bet offers for specified event. Type can be used to filter the bet offers by bet offer type.
   * @memberOf KambiApi
   * @param {number[]} eventIds - Array of event IDS to get.
   * @param {string} locale - Defines what language should be used for localized texts. Example en_GB, fr_FR.
   * @param {string} market - Defines the geographical market that the player is registered in. Example GB, FR.
   * @param {string} offering - Unique identifier assigned to the API user. Example: kambi.
   * @param {number} type - Bet offer type.
   * @param {*} logData - log data TODO needs an object type.
   * @param {boolean} [useProdApis=false] - Uses prod API when true.
   * @return {Promise<KBetOfferResponse>} - Events and bet offers arrays.
   */
  getBetOffersForEvents: function(
    eventIds = [],
    locale,
    market,
    offering,
    type,
    logData,
    useProdApis = false
  ) {
    const kambiApiUrl = getOfferingApi(useProdApis);
    const url = `${kambiApiUrl}/${offering}/betoffer/event/${eventIds.join(
      ','
    )}`;
    const params = { lang: locale, market, includeParticipants: false, type };

    return new Http(OfferingApiAxiosInstance.get())
      .get(logData, url, params, true)
      .then(response => response.data);
  },

  /**
   * Get events that have participant icons (silks).
   * @memberOf KambiApi
   * @param {number[]} eventIds - Array of event IDS to get.
   * @param {string} offering - Unique identifier assigned to the API user. Example: kambi.
   * @param {*} logData - log data TODO needs an object type.
   * @param {boolean} [useProdApis=false] - Uses prod API when true.
   * @return {Promise<KEventResponse>} - Array of events.
   */
  getEventsWithIcons: function(
    eventIds = [],
    offering,
    logData,
    useProdApis = false
  ) {
    const kambiApiUrl = getOfferingApi(useProdApis);
    const url = `${kambiApiUrl}/${offering}/event/icon?${eventIds
      .map(eventId => `id=${eventId}`)
      .join('&')}`;

    return new Http(OfferingApiAxiosInstance.get())
      .get(logData, url, {}, true)
      .then(response => response.data);
  },

  getEventsForHighlights: function(
    locale,
    market,
    offering,
    logData,
    useProdApis = false
  ) {
    const kambiApiUrl = getOfferingApi(useProdApis);
    const url = `${kambiApiUrl}/${offering}/betoffer/landing`;
    const params = { lang: locale, market };

    return new Http(OfferingApiAxiosInstance.get())
      .get(logData, url, params, true)
      .then(response => response.data);
  },

  getBetOffersForHighlights: function(
    eventIds,
    locale,
    market,
    offering,
    logData,
    useProdApis = false
  ) {
    const kambiApiUrl = getOfferingApi(useProdApis);
    let url = `${kambiApiUrl}/${offering}/betoffer/selected/main_pre_match_bet_offer/event/`;
    const params = { lang: locale, market };
    eventIds.forEach((value, index) => {
      if (index === 0) {
        url = url + value.eventId + '?';
      } else {
        url = url + 'id=' + value.eventId + '&';
      }
    });

    return new Http(OfferingApiAxiosInstance.get())
      .get(logData, url, params, true)
      .then(response => response.data);
  },

  getOpenBets: function(userContext, useProdApis = false) {
    const { offering, kambiSessionId, brand } = userContext;
    const kambiApiUrl = getPlayerApi(brand, useProdApis);
    const params = { status: 'SCF_PENDING', range_size: 999 };
    const url = `${kambiApiUrl}/${offering}/coupon/summary.json`;
    const logData = getLogDataFromUserContext(userContext);

    return new Http(PlayerApiAxiosInstance.get())
      .get(logData, url, params, true, { Authorization: `Bearer ${kambiSessionId}` })
      .then(response => response.data);
  },

  getCashOutBets: function(userContext, useProdApis = false) {
    const { offering, kambiSessionId, brand } = userContext;
    const kambiApiUrl = getPlayerApi(brand, useProdApis);
    const url = `${kambiApiUrl}/${offering}/cashout.json`;
    const logData = getLogDataFromUserContext(userContext);

    return new Http(PlayerApiAxiosInstance.get())
      .get(logData, url, {}, true, { Authorization: `Bearer ${kambiSessionId}` })
      .then(response => response.data);
  },

  login: function(userContext, ticket, useProdApis = false) {
    const {
      locale,
      market,
      brand: customerData,
      offering,
      streamingAllowedForPlayer: streamingAllowed,
      brand
    } = userContext;
    const kambiApiUrl = getPlayerApi(brand, useProdApis);
    const url = `${kambiApiUrl}/${offering}/punter/login.json`;
    const params = { lang: locale, settings: false };
    const data = {
      ticket,
      market,
      customerData,
      streamingAllowed,
      punterId: ''
    };
    const logData = getLogDataFromUserContext(userContext);

    return new Http(PlayerApiAxiosInstance.get())
      .post(logData, url, data, params, true)
      .then(response => response.data);
  },

  extendSession: function(userContext, useProdApis = false) {
    const { offering, kambiSessionId, brand } = userContext;
    const kambiApiUrl = getPlayerApi(brand, useProdApis);
    const params = {
      logData: getLogDataFromUserContext(userContext),
      url: `${kambiApiUrl}/${offering}/punter/session.json`,
      data: {},
      params: {},
      logToClickstream: true,
      headers: { Authorization: `Bearer ${kambiSessionId}` }
    };

    return extendSessionRetry(ExtendSessionAxiosInstance, 'post', params);
  },

  //TODO: add brand parameter when this function is called
  makeCashInRequest: function(/*payload, brand, useProdApis = false*/) {
    //const {singleBetId, kambiSessionId, potentialPayout, offering} = payload;
    //const kambiPlayerApi = getPlayerApi(brand, useProdApis);
    //const url = `/${offering}/coupon/bet/${singleBetId}/cashIn.json;jsessionid=${kambiSessionId}`;
    //const data = {"singleBetId": singleBetId, "cashInRequest": {"amount": potentialPayout}};
    /*// TODO move to http axios wrapper
    return axios.post(url, data, {timeout: Global.config.axiosTimeout})
      .then(function (response) { //TODO: add log to clickstream
        return response.data;
      }).catch((err) => {
        return err;
      });*/
  }
};
